// Shared energy module — per-activity helpers (signedEnergyDelta /
// keepEnergyState / energyCellTintClass) AND the aggregate Energy Score
// computations consumed by /results, /audit, /, and the lead-temperature
// classifier in /api/generate-ai-report. ONE source of truth for "Energy
// Score Before" and "Energy Score After" so the customer-facing card and
// the classifier's energy input can never drift (TEMP-001, 2026-05-17).
//
// Before this consolidation, each page reproduced the same beforeEnergy /
// totalActiveHoursForEnergy / normalize-to-[-100,+100] reduction inline.
// Three copies, same parallel-implementation drift class as the rate-
// cascade bugs (BOTTLENECK-001, RETURN-001/003). The aggregate Energy
// Score now flows through the helpers below.
//
// Per-activity signed energy delta.
//
// Returns the energy units gained (positive) or given up (negative) by
// removing this activity from the founder's plate. Sign convention matches
// the aggregate Energy Score numerator at src/app/results/page.tsx:434-438
// so per-row values are commensurate with aggregate movement.
//
//   ei = -2 (drain) × 8 hrs → -(8 × -2) = +16  drain relief
//   ei =  0 (neutral) × 20 hrs →  0              honest: no energy effect
//   ei = +1 (energizing) × 4 hrs → -(4 × 1) = -4  warning: giving up energy
//
// Replaces the prior `(allocatedTime × 3) - (allocatedTime × ei)` formula
// which was always ≥ 0 (over-counted neutral delegations as gains) and used
// `min(va, diy)` as hours basis (mismatched the aggregate's diy basis).

export function signedEnergyDelta(diyHoursMonthly: number, energyImpact: number): number {
  // `|| 0` collapses JavaScript's -0 (from -(N × 0)) to +0 so downstream
  // consumers and tests get a canonical zero.
  return -(diyHoursMonthly * energyImpact) || 0;
}

// Current-state energy of a Keep-bucket activity ON the founder's plate.
//
// SIGN CONVENTION DIFFERS FROM signedEnergyDelta INTENTIONALLY — they measure
// different things, and the per-column header tooltip on each table explains
// which is which:
//
//   signedEnergyDelta = -(diy * ei)  → "energy effect of REMOVING this activity"
//     used in Delegated/Drop/MustDelegate columns. Positive = drain recovered,
//     negative = energizing work given up.
//
//   keepEnergyState   = +(diy * ei)  → "energy state of work STAYING on plate"
//     used in Keep column only. Positive = energizing work kept (good signal),
//     negative = drain being absorbed (the warning case — these are
//     re-route candidates).
//
// Same magnitudes per row in both formulas; only the sign flips. A row's
// keepEnergyState contribution to the aggregate Energy Score Before/After
// numerator is direct: the aggregate uses +(diy * ei), commensurate with this.
export function keepEnergyState(diyHoursMonthly: number, energyImpact: number): number {
  return diyHoursMonthly * energyImpact || 0;
}

// Faint cell tint for energy columns — visual signal in addition to the
// amber/yellow text color. Returns a className string suitable for Ant
// Design Table's onCell return. Tuned to read as "barely there" in both
// light and dark mode without competing with row-hover or other in-row
// color (e.g., the emerald $ values in the Delegated table).
//   positive → faint emerald wash
//   zero     → no tint (cell renders plain)
//   negative → faint amber wash
// Sign meaning per column comes from the formula feeding it
// (signedEnergyDelta vs keepEnergyState) — the tint just amplifies sign.
export function energyCellTintClass(value: number): string {
  if (value > 0) return "bg-emerald-500/5 dark:bg-emerald-400/10";
  if (value < 0) return "bg-amber-500/5 dark:bg-amber-400/10";
  return "";
}

// =====================================================================
// Aggregate Energy Score helpers (TEMP-001, 2026-05-17)
// =====================================================================
//
// Two pure functions producing the customer-facing -100..+100 Energy Score
// shown on the Energy Score KPI card across /, /audit, and /results. Same
// math as the prior inline reductions; centralized so the lead-temperature
// classifier in /api/generate-ai-report consumes the same number the
// customer sees.
//
// "Before": current weekly state across ALL currently-active hours.
// "After":  projected post-delegation state, excluding Delegate/Drop/
//   MustDelegate activities (those come off the founder's plate entirely).

import { getFinalResolution } from "./resolution";

// Minimal-shape input — matches the on-the-wire activity record shape from
// /api/delegation. After-state needs every field getFinalResolution touches
// (force_*, ill_do_it, resolution, currently_doing, enjoyment_rating,
// revenue_impact, complexity, is_important, is_urgent, is_mandatory,
// strategic_pillar, activity_goals) — modeled openly via index signature
// rather than enumerated, since these helpers are the boundary between
// activity-blobs-from-DB and aggregate numbers.
export interface EnergyActivity {
  energy_impact?: number | null;
  delegation_plan?: {
    rova_tracker?: { diy_hours_monthly?: number | string | null } | null;
  } | null;
  [key: string]: any;
}

/**
 * "Before" Energy Score — the current-state signed magnitude of energy
 * across all currently-active hours. Range: [-100, +100].
 *   -100 = max drain (every hour at energy_impact -2)
 *   +100 = max energizing (every hour at energy_impact +2)
 *     0  = neutral OR empty session (per fallback)
 *
 * Pure function. No session context required.
 */
export function computeEnergyScoreBefore(activities: EnergyActivity[]): number {
  let netBalance = 0;
  let totalHours = 0;
  for (const a of activities) {
    const hrs = Number(a.delegation_plan?.rova_tracker?.diy_hours_monthly) || 0;
    const impact = a.energy_impact || 0;
    netBalance += hrs * impact;
    totalHours += hrs;
  }
  const maxPossible = totalHours * 2;
  if (maxPossible <= 0) return 0;
  return Math.max(-100, Math.min(100, Math.round((netBalance / maxPossible) * 100)));
}

/**
 * "After" Energy Score — projected weekly state after the founder's plan
 * executes. Excludes activities resolved to Delegate/Drop/MustDelegate
 * (those leave the founder's plate). Range: [-100, +100].
 *
 * Fallback semantics (preserved from prior inline impl):
 *   - after-denominator > 0  → normalized signed score
 *   - after-denominator = 0 but before-denominator > 0 → +100 (perfect plan:
 *     founder delegated/dropped all active hours; nothing remains to drain)
 *   - both zero → 0 (empty session)
 */
export function computeEnergyScoreAfter(
  activities: EnergyActivity[],
  sessionContext: { team_size_band: string | null; gross_revenue_band: string | null },
): number {
  let beforeTotalHours = 0;
  let afterTotalHours = 0;
  let afterNetBalance = 0;
  for (const a of activities) {
    const hrs = Number(a.delegation_plan?.rova_tracker?.diy_hours_monthly) || 0;
    const impact = a.energy_impact || 0;
    beforeTotalHours += hrs;
    const res = getFinalResolution(a as any, sessionContext);
    if (res === "Delegate" || res === "Drop" || res === "MustDelegate") continue;
    afterTotalHours += hrs;
    afterNetBalance += hrs * impact;
  }
  const afterMaxPossible = afterTotalHours * 2;
  if (afterMaxPossible > 0) {
    return Math.max(-100, Math.min(100, Math.round((afterNetBalance / afterMaxPossible) * 100)));
  }
  return beforeTotalHours > 0 ? 100 : 0;
}
